import { FC, memo } from 'react'
import styled, { useTheme } from 'styled-components'
import { DateTime } from 'luxon'
import Trans from 'next-translate/Trans'
import { useRouter } from 'next/router'
import { Delivery, ProductSize, TimeUnit } from '@/network/graphql.g'
import { Flex } from '@/components/atoms/Grid'
import { Small2 } from '@/components/atoms/typography'
import { CheckIconDefault } from '@/components/atoms/icons/CheckIconDefault'
import { validFor } from '@/theme/validFor'
import { addWorkingDays } from '@/core/deliveryCalendar'
import { useCountryIsoCode } from '@/providers/storeProvider'

type DeliveryDates = {
  from: string
  to?: string
}

type ProductDetailStockDeliveryNewProps = {
  sizes: ProductSize[]
  selectedSize?: ProductSize
  deliveryDates?: DeliveryDates
}

const ProductDetailStockDeliveryNew: FC<ProductDetailStockDeliveryNewProps> = ({
  sizes,
  selectedSize,
  deliveryDates
}) => {
  const theme = useTheme()
  const { locale } = useRouter()
  const countryIsoCode = useCountryIsoCode()

  const dates =
    deliveryDates ??
    makeDeliveryDates(sizes, selectedSize, locale, countryIsoCode)

  if (!dates) {
    return null
  }

  return (
    <DeliveryRow alignItems="center" data-testid="productDetailStockDelivery">
      <StyledCheckIcon
        width={18}
        height={18}
        color={theme.colors.accentSecondary}
      />
      <DeliveryText m={0}>
        <Trans
          i18nKey={
            dates.to
              ? 'common:ProductDetail.Delivery.inStockRange'
              : 'common:ProductDetail.Delivery.inStockFrom'
          }
          components={[<InStock />]}
          values={{ from: dates.from, to: dates.to }}
        />
      </DeliveryText>
    </DeliveryRow>
  )
}

export default memo(ProductDetailStockDeliveryNew)

const toDays = (value: number, unit: TimeUnit): number =>
  unit === TimeUnit.Hours ? Math.ceil(value / 24) : value

const toDayRange = (
  delivery?: Delivery
): { lowest: number; highest: number } => {
  if (!delivery || typeof delivery.highest !== 'number') {
    return null
  }

  const highest = toDays(delivery.highest, delivery.unit)
  const lowest =
    typeof delivery.lowest === 'number'
      ? toDays(delivery.lowest, delivery.unit)
      : highest

  return {
    lowest: Math.min(lowest, highest),
    highest: Math.max(lowest, highest)
  }
}

const makeDeliveryDates = (
  sizes: ProductSize[],
  selectedSize: ProductSize,
  locale: string,
  country: string
): DeliveryDates => {
  const formatDate = (days: number) =>
    addWorkingDays(DateTime.now(), days, country)
      .setLocale(locale)
      .toLocaleString({ day: 'numeric', month: 'numeric' })

  const selectedRange = toDayRange(selectedSize?.delivery)

  if (selectedRange) {
    return {
      from: formatDate(selectedRange.lowest),
      to:
        selectedRange.highest > selectedRange.lowest
          ? formatDate(selectedRange.highest)
          : undefined
    }
  }

  const soonest = sizes
    ?.map((size) => toDayRange(size?.delivery))
    .filter(Boolean)
    .sort((a, b) => a.lowest - b.lowest)[0]

  return soonest ? { from: formatDate(soonest.lowest) } : null
}

const DeliveryRow = styled(Flex)`
  gap: 8px;
  ${validFor.mobile`
    gap: 4px;
  `}
`

const StyledCheckIcon = styled(CheckIconDefault)`
  flex-shrink: 0;
  ${validFor.mobile`
    width: 24px;
    height: 18px;
  `}
`

const DeliveryText = styled(Small2)`
  font-size: 12px;
  letter-spacing: -0.12px;
`

const InStock = styled.span`
  font-weight: bold;
`
